import { useState, useEffect } from "react";
import { jwtFetch } from "./utils/apiClient";
import { useParams, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { config, encodeBase64RequestBody, priceFormat, numberFormat, shortenUrl } from "./Components/Config";
import Menu from "./Components/Menu";
import Header from "./Components/Header";

const InvoiceRequestDetail = () => {
  const { id_iou } = useParams();
  const [iou, setIou] = useState(null);
  const [iouList, setIouList] = useState(null);
  const [quotation, setQuotation] = useState(null);
  const [quotationItems, setQuotationItems] = useState([]);
  const [quotationItemList, setQuotationItemList] = useState([]);
  const [quotationAttachmentList, setQuotationAttachmentList] = useState([]);
  const [quotationPaymentRate, setQuotationPaymentRate] = useState(100);
  const [salesOrder, setSalesOrder] = useState(null);
  const [currency, setCurrency] = useState('IDR');
  const [iouTotal, setIouTotal] = useState(0);
  const [exchangeRate, setExchangeRate] = useState(0);
  const [subcompanyList, setSubcompanyList] = useState(null);
  const [isEditable, setIsEditable] = useState(true);
  const [isEditing, setIsEditing] = useState(true);
  const [isLoadingSubmit, setIsLoadingSubmit] = useState(false);
  const [invoiceAttachments, setInvoiceAttachments] = useState(null);
  const crmUser = JSON.parse(localStorage.getItem('crmUser'));

  const navigate = useNavigate();

  useEffect(() => {
    getIou();
  }, []);

  useEffect(() => {
    if (iou) {
      getQuotation();
      getSalesOrder();
    }
  }, [iou]);

  useEffect(() => {
    if (quotation) {
      getSubcompanyList();
    }
  }, [quotation]);

  useEffect(() => {
    if (iouList && quotation && exchangeRate) {
      const updatedIouList = iouList;
      for (let iouIndex in updatedIouList) {
        let iouSubtotal = 0;
        for (let iouItemIndex in updatedIouList[iouIndex].iou_items) {
          let iouItem = updatedIouList[iouIndex].iou_items[iouItemIndex];
          if (iouItem.product_id) {
            let quotationItem = quotation.quotation_item_list.find(item => parseInt(item.product_id) === parseInt(iouItem.product_id));

            iouItem.product_cost_original = parseFloat(quotationItem.product_cost_original);
            iouItem.product_price_original = parseFloat(quotationItem.product_price_original);
            iouItem.product_cost = iouItem.product_currency_original !== currency ? (quotationItem.product_cost_original * exchangeRate) : parseFloat(quotationItem.product_cost);
            iouItem.product_price = iouItem.product_currency_original !== currency ? (quotationItem.product_price_original * exchangeRate) : parseFloat(quotationItem.product_price);
            iouItem.product_subtotal = parseFloat((iouItem.product_price * iouItem.product_qty * iouItem.contract_long_period).toFixed(3));
            iouItem.product_prorate = parseFloat((iouItem.product_qty / parseFloat(quotationItem.product_qty) * 100).toFixed(3));
            iouItem.product_discount_price = parseFloat(((quotationItem.product_discount_price * iouItem.product_prorate / 100) * (iouItem.contract_long_period / quotationItem.contract_long_period)).toFixed(3));
            iouItem.product_discount_rate = parseFloat((iouItem.product_discount_price / iouItem.product_subtotal * 100).toFixed(3));
            iouItem.product_total = parseFloat((iouItem.product_subtotal - iouItem.product_discount_price).toFixed(3));
            iouItem.product_total_prorate = iouItem.product_total / parseFloat(quotation.quotation_subtotal) * 100;
            iouItem.product_discount_prorate = parseFloat((iouItem.product_total_prorate * quotation.quotation_discount_price / 100).toFixed(3));
            iouItem.product_grand_total = parseFloat(((iouItem.product_total - iouItem.product_discount_prorate) * quotationPaymentRate / 100).toFixed(3));
            iouItem.product_margin_price = parseFloat((iouItem.product_grand_total - (iouItem.product_cost * iouItem.product_qty * iouItem.contract_long_period * quotationPaymentRate / 100)).toFixed(3));
            iouItem.product_margin_rate = iouItem.product_grand_total > 0 ? parseFloat((iouItem.product_margin_price / iouItem.product_grand_total * 100).toFixed(3)) : 0;

            iouSubtotal += parseFloat((iouItem.product_grand_total).toFixed(3));
          }
        }
        updatedIouList[iouIndex].iou_exchange_rate = exchangeRate;
        updatedIouList[iouIndex].iou_subtotal = iouSubtotal;
        updatedIouList[iouIndex].iou_total = iouSubtotal;
        updatedIouList[iouIndex].iou_tax_price = parseFloat((iouSubtotal * 12 / 100).toFixed(3));
        updatedIouList[iouIndex].iou_grand_total = parseFloat((iouSubtotal + updatedIouList[iouIndex].iou_tax_price).toFixed(3));
      }
      if (JSON.stringify(updatedIouList) !== JSON.stringify(iouList)) {
        setIouList(updatedIouList);
      }
    }
  }, [quotation, iouList, exchangeRate]);

  const getIou = async () => {
    try {
      const fetchGetIou = await jwtFetch(config.ENDPOINT + 'get/iou', {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id_iou: id_iou
        })
      });
      const responseGetIou = await fetchGetIou.json();
      if (responseGetIou.data) {
        responseGetIou.data[0].request_invoice_pillar = responseGetIou.data[0].request_invoice_pillar || 'Non-Bundling';
        responseGetIou.data[0].request_invoice_type = responseGetIou.data[0].request_invoice_type || 'New Order';
        responseGetIou.data[0].request_invoice_company_name = responseGetIou.data[0].request_invoice_company_name || responseGetIou.data[0].subcompany_name;
        responseGetIou.data[0].request_invoice_company_address = responseGetIou.data[0].request_invoice_company_address || responseGetIou.data[0].subcompany_address;
        responseGetIou.data[0].request_invoice_company_phone = responseGetIou.data[0].request_invoice_company_phone || responseGetIou.data[0].subcompany_phone;
        responseGetIou.data[0].iou_items = await getIouItemList(responseGetIou.data[0].id_iou);
        if (responseGetIou.data[0].iou_status === 'New') {
          responseGetIou.data[0].request_invoice_documents = ['Invoice', 'Faktur Pajak', 'Receipt/Kwitansi'];
          responseGetIou.data[0].request_invoice_attachments = [...quotationAttachmentList];
        } else {
          responseGetIou.data[0].request_invoice_documents = JSON.parse(responseGetIou.data[0].request_invoice_documents) || [];
          responseGetIou.data[0].request_invoice_attachments = JSON.parse(responseGetIou.data[0].request_invoice_attachments) || [];
        }
        if (responseGetIou.data[0].iou_status === 'Notification Sent') {
          setIsEditable(false);
          setIsEditing(false);
        }
        setCurrency(responseGetIou.data[0].iou_currency);
        setIouTotal(responseGetIou.data[0].iou_grand_total);
        setExchangeRate(responseGetIou.data[0].iou_exchange_rate);

        setIou(responseGetIou.data[0]);
      } else {
        alert(responseGetIou.message);
      }
    } catch (error) {
      console.error('Error in getIou:', error);
      alert('An error occurred: ' + (error.message || error));
    }
  };

  const getQuotation = async () => {
    try {
      const fetchGetQuotation = await jwtFetch(config.ENDPOINT + 'get/quotation', {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id_quotation: iou.quotation_id
        })
      });
      const responseGetQuotation = await fetchGetQuotation.json();
      if (responseGetQuotation.data) {
        if (responseGetQuotation.data[0].attachment_files) {
          let attachmentFiles = JSON.parse(responseGetQuotation.data[0].attachment_files);
          for (let a in attachmentFiles) {
            attachmentFiles[a].status = 'Ada & Terlampir';
          }
          setQuotationAttachmentList([...quotationAttachmentList, ...attachmentFiles]);
        }

        if (responseGetQuotation.data[0].quotation_items) {
          let itemList = responseGetQuotation.data[0].quotation_items;
          for (let i in itemList) {
            if (itemList[i].billing_payment_period === 'Custom') {
              for (let p in itemList[i].payment_simulations) {
                if (itemList[i].payment_simulations[p].term_number === iou.term_number) {
                  setQuotationPaymentRate(parseFloat(itemList[i].payment_simulations[p].payment_rate));
                }
              }
            }
          }
        }

        setQuotationItems([...quotationItems, ...responseGetQuotation.data[0].quotation_items]);
        setQuotationItemList([...quotationItemList, ...responseGetQuotation.data[0].quotation_item_list]);
        setQuotation(responseGetQuotation.data[0]);
      } else {
        alert(responseGetQuotation.message);
      }
    } catch (error) {
      console.error('Error in getQuotation:', error);
      alert('An error occurred: ' + (error.message || error));
    }
  };

  const getSalesOrder = async () => {
    try {
      const fetchGetSalesOrder = await jwtFetch(config.ENDPOINT + 'get/so', {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id_so: iou.so_id
        })
      });
      const responseGetSalesOrder = await fetchGetSalesOrder.json();
      if (responseGetSalesOrder.data) {
        setSalesOrder(responseGetSalesOrder.data[0]);
      } else {
        alert(responseGetSalesOrder.message);
      }
    } catch (error) {
      console.error('Error in getSalesOrder:', error);
      alert('An error occurred: ' + (error.message || error));
    }
  };

  const getIouItemList = async (iou_id) => {
    try {
      const requestIouList = {
        iou_id: iou_id
      }
      const fetchIouList = await jwtFetch(config.ENDPOINT + 'get/iou_item', {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestIouList)
      });
      const responseIouList = await fetchIouList.json();
      if (responseIouList.status === 200) {
        const newIouItems = [];
        for (let i in responseIouList.data) {
          let newIouItem = responseIouList.data[i];
          const companyLicence = await getCompanyLicence(responseIouList.data[i].company_id, responseIouList.data[i].product_id, responseIouList.data[i].product_domain);
          if (companyLicence) {
            newIouItem.licence_start_period = companyLicence.licence_start_period;
            newIouItem.licence_end_period = companyLicence.licence_end_period;
            newIouItem.action = 'Update';
          }
          newIouItems.push(newIouItem);
        }
        return newIouItems;
      } else {
        alert(responseIouList.message);
      }
    } catch (error) {
      console.error('Error in getIouItemList:', error);
      alert('An error occurred: ' + (error.message || error));
    }
  }

  const getCompanyLicence = async (companyId, productId, licenceDomain) => {
    try {
      const requestCompanyLicence = {
        "company_id": companyId,
        "product_id": productId,
        "licence_domain": licenceDomain,
      }
      const fetchCompanyLicence = await jwtFetch(config.ENDPOINT + 'get/clients_company_licence', {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestCompanyLicence)
      });
      const responseCompanyLicence = await fetchCompanyLicence.json();
      if (responseCompanyLicence.status === 200) {
        if (responseCompanyLicence.data.length > 0) {
          return responseCompanyLicence.data[0];
        } else {
          return null;
        }
      } else {
        alert(responseCompanyLicence.message);
      }
    } catch (error) {
      console.error('Error in getCompanyLicence:', error);
      alert('An error occurred: ' + (error.message || error));
    }
  }

  const getSubcompanyList = async () => {
    try {
      const requestSubcompanyList = {
        company_id: quotation.company_id
      }
      const fetchSubcompanyList = await jwtFetch(config.ENDPOINT + 'get/subcompany', {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
        },
        body: encodeBase64RequestBody(requestSubcompanyList)
      });
      const responseSubcompanyList = await fetchSubcompanyList.json();
      if (responseSubcompanyList.status === 200) {
        setSubcompanyList(responseSubcompanyList.data);
      } else {
        alert(responseSubcompanyList.message);
      }
    } catch (error) {
      console.error('Error in getSubcompanyList:', error);
      alert('An error occurred: ' + (error.message || error));
    }
  }

  const changeIouData = (column, value) => {
    let newIou = { ...iou };
    newIou[column] = value;
    setIou(newIou);
  }

  const copyItem = (itemIndex) => {
    let description = iou.iou_items[itemIndex].product_display_name;
    description += '\n\n';
    description += iou.iou_items[itemIndex].product_display_description;
    description += '\n\n';
    description += 'This QT Order: ' + iou.quotation_code + '\n';
    description += iou.service_type === 'Additional' ? 'Period - Current Active Subscription: ' + (format(new Date(iou.iou_items[itemIndex].licence_start_period), 'd MMMM yyyy') + " - " + format(new Date(iou.iou_items[itemIndex].licence_end_period), 'd MMMM yyyy')) + '\n' : '';
    description += 'Period - for this QT Order: ' + (format(new Date(iou.iou_items[itemIndex].start_term_period), 'd MMMM yyyy') + " - " + format(new Date(iou.iou_items[itemIndex].end_term_period), 'd MMMM yyyy')) + '\n';
    description += 'Period - Term Payment: ' + (format(new Date(iou.start_term_period), 'd MMMM yyyy') + " - " + format(new Date(iou.end_term_period), 'd MMMM yyyy')) + '\n';
    description += 'Schedule: ' + (iou.term_number.replace('Term ', '') + " of " + iou.term_count) + '\n';
    description += '#User of this QT: ' + iou.iou_items[itemIndex].product_qty + ' Unit \n';
    navigator.clipboard.writeText(description);
  }

  const updateInvoice = async () => {
    try {
      setIsLoadingSubmit(true);

      if (!iou.invoice_number) {
        alert('Invoice Number is required');
        setIsLoadingSubmit(false);
        return;
      }

      if (!iou.invoice_amount) {
        alert('Invoice Amount is required');
        setIsLoadingSubmit(false);
        return;
      }

      if (!iou.invoice_due_date) {
        alert('Invoice Due Date is required');
        setIsLoadingSubmit(false);
        return;
      }

      // The invoice form is locked once the status moves on, so a failed
      // upload must stop here rather than issue the invoice without its file.
      let invoiceAttachmentsData = null;
      if (invoiceAttachments) {
        const responseUploadInvoiceAttachments = await uploadInvoiceAttachments();
        if (!responseUploadInvoiceAttachments) {
          setIsLoadingSubmit(false);
          return;
        }
        invoiceAttachmentsData = JSON.stringify(responseUploadInvoiceAttachments);
      }

      const crmUser = JSON.parse(localStorage.getItem('crmUser'));
      const requestInvoice = {
        id_iou: iou.id_iou,
        iou_status: 'Invoice Issued',
        invoice_number: iou.invoice_number,
        invoice_amount: iou.invoice_amount,
        invoice_attachments: invoiceAttachmentsData,
        invoice_due_date: iou.invoice_due_date,
        invoice_input_date: format(new Date(), 'yyyy-MM-dd HH:mm:ss'),
        invoice_input_by: crmUser.user_keyid,
      };
      const fetchInvoice = await jwtFetch(config.ENDPOINT + 'update/iou', {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestInvoice)
      });
      const responseInvoice = await fetchInvoice.json();
      if (responseInvoice.status === 200) {
        window.location.href = '/invoicerequest/detail/' + iou.id_iou;
      } else {
        alert(responseInvoice.message);
      }
    } catch (error) {
      console.error('Error in updateInvoice:', error);
      alert('An error occurred: ' + (error.message || error));
      setIsLoadingSubmit(false);
    }
  }

  const updatePayment = async () => {
    try {
      setIsLoadingSubmit(true);

      if (!iou.payment_paid_date) {
        alert('Paid Date is required');
        setIsLoadingSubmit(false);
        return;
      }

      const crmUser = JSON.parse(localStorage.getItem('crmUser'));
      const requestPayment = {
        id_iou: iou.id_iou,
        iou_status: 'Paid in Full',
        payment_paid_date: iou.payment_paid_date,
        payment_input_date: format(new Date(), 'yyyy-MM-dd HH:mm:ss'),
        payment_input_by: crmUser.user_keyid,
      };
      const fetchPayment = await jwtFetch(config.ENDPOINT + 'update/iou', {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestPayment)
      });
      const responsePayment = await fetchPayment.json();
      if (responsePayment.status === 200) {
        window.location.href = '/invoicerequest/detail/' + iou.id_iou;
      } else {
        alert(responsePayment.message);
      }
    } catch (error) {
      console.error('Error in updatePayment:', error);
      alert('An error occurred: ' + (error.message || error));
      setIsLoadingSubmit(false);
    }
  }

  const updateInvoiceAttachments = async () => {
    try {
      setIsLoadingSubmit(true);

      if (!invoiceAttachments) {
        alert('File is required');
        setIsLoadingSubmit(false);
        return;
      }

      const responseUploadInvoiceAttachments = await uploadInvoiceAttachments();
      if (!responseUploadInvoiceAttachments) {
        setIsLoadingSubmit(false);
        return;
      }

      const requestInvoiceAttachments = {
        id_iou: iou.id_iou,
        invoice_attachments: JSON.stringify(responseUploadInvoiceAttachments),
      };
      const fetchInvoiceAttachments = await jwtFetch(config.ENDPOINT + 'update/iou', {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestInvoiceAttachments)
      });
      const responseInvoiceAttachments = await fetchInvoiceAttachments.json();
      if (responseInvoiceAttachments.status === 200) {
        window.location.href = '/invoicerequest/detail/' + iou.id_iou;
      } else {
        alert(responseInvoiceAttachments.message);
        setIsLoadingSubmit(false);
      }
    } catch (error) {
      console.error('Error in updateInvoiceAttachments:', error);
      alert('An error occurred: ' + (error.message || error));
      setIsLoadingSubmit(false);
    }
  }

  const uploadInvoiceAttachments = async () => {
    const formData = new FormData();
    invoiceAttachments.forEach((attachment) => {
      formData.append('files', attachment);
    });
    try {
      const responseUploadInvoiceAttachments = await jwtFetch(config.ENDPOINT + 'upload/gdrive', {
        method: 'POST',
        body: formData,
      });
      if (!responseUploadInvoiceAttachments.ok) {
        throw new Error(`Upload failed with status: ${responseUploadInvoiceAttachments.status}`);
      }
      const resultUploadInvoiceAttachments = await responseUploadInvoiceAttachments.json();
      if (resultUploadInvoiceAttachments.status === 200) {
        return resultUploadInvoiceAttachments.data;
      } else {
        alert(`Upload error: ${resultUploadInvoiceAttachments.message}`);
      }
    } catch (error) {
      alert(`Upload error: ${error.message || error}`);
    }
  };

  return (
    <div id="invoicerequest-detail">
      <div className="container-fluid p-0">
        <div className="row g-0 mh-100vh">
          <div className="col-md-2 border-end">
            <div className="d-md-flex d-none align-items-center justify-content-center p-1 h-48">
              <a className="h-100" href="/dashboard"><img className="h-100" src="/cuancuan.png" alt="PointStar" /></a>
            </div>
            <hr className="m-0" />
            <Menu active="invoicerequest" />
          </div>
          <div className="col-md-10">
            <Header />
            <hr className="m-0" />
            <div className="main">
              <div className="p-4">

                {quotation && salesOrder && iou && (
                  <section className="mb-4">
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <div className="d-flex align-items-center">
                        <div className="fs-24"><button className="btn btn-link" onClick={() => navigate(-1)}><i className="bi bi-arrow-left" /></button> Invoice Request Detail</div>
                        {iou.iou_status === 'Notification Sent' && (
                          <span className="badge badge-sm ms-3 text-dark text-bg-info">Requested to Finance</span>
                        )}
                        {iou.iou_status === 'Invoice Issued' && (
                          <span className="badge badge-sm ms-3 text-dark text-bg-gold">Invoice Issued</span>
                        )}
                        {iou.iou_status === 'Stopped' && (
                          <span className="badge badge-sm ms-3 text-light text-bg-danger">Stopped</span>
                        )}
                        {iou.iou_status === 'Paid in Full' && (
                          <span className="badge badge-sm ms-3 text-light text-bg-success">Paid in Full</span>
                        )}
                      </div>
                      <div className="d-flex align-items-center justify-content-between gap-2">
                        {!isEditing && isEditable && (
                          <button className="btn btn-sm btn-outline-secondary" onClick={() => setIsEditing(true)}><i className="bi bi-pencil" /> Edit</button>
                        )}
                        {iou.iou_status === 'Notification Sent' && (<a className="btn btn-sm btn-outline-secondary" href={"/invoicerequest/print/" + iou.id_iou} target="_blank" rel="noopener noreferrer"><i className="bi bi-printer me-2" /> Print</a>)}
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-md-6">
                        <div className="row mb-2">
                          <div className="col-md-4 text-secondary">Number</div>
                          <div className="col-md-8">{iou.iou_code} <i className="bi bi-copy pointer" onClick={() => navigator.clipboard.writeText(iou.iou_code)} /></div>
                        </div>
                        <div className="row mb-2">
                          <div className="col-md-4 text-secondary">Company Group</div>
                          <div className="col-md-8">{iou.company_name} <a href={`/client/detail/${iou.company_id}`}><i className="bi bi-link-45deg" /></a></div>
                        </div>
                        <div className="row mb-2">
                          <div className="col-md-4 text-secondary">Subsidiary</div>
                          <div className="col-md-8">{iou.subcompany_name} <a href={`/client/subsidiary/detail/${iou.subcompany_id}`}><i className="bi bi-link-45deg" /></a></div>
                        </div>
                        <div className="row mb-2">
                          <div className="col-md-4 text-secondary">Currency</div>
                          <div className="col-md-8">{iou.iou_currency}</div>
                        </div>
                        <div className="row align-items-baseline mb-2">
                          <div className="col-md-4 text-secondary">Exchange Rate</div>
                          <div className="col-md-8">1 USD <i className="bi bi-arrow-right mx-1" /> {priceFormat(exchangeRate, iou.iou_currency)} ({quotation && quotation.quotation_exchange_floating})</div>
                        </div>
                        <div className="row mb-2">
                          <div className="col-md-4 text-secondary">Schedule</div>
                          <div className="col-md-8">{iou.term_number.replace('Term', 'Schedule')}</div>
                        </div>
                        <div className="row mb-2">
                          <div className="col-md-4 text-secondary">Period Date</div>
                          <div className="col-md-8">{iou.start_term_period === iou.end_term_period ? format(iou.start_term_period, 'd MMM yyyy') : (format(iou.start_term_period, 'd MMM yyyy') + " - " + format(iou.end_term_period, 'd MMM yyyy'))}</div>
                        </div>
                        <div className="row mb-2">
                          <div className="col-md-4 text-secondary">Created Date</div>
                          <div className="col-md-8">{format(iou.created_on, 'd MMM yyyy')}</div>
                        </div>
                        {quotation && (
                          <div className="row mb-2">
                            <div className="col-md-4 text-secondary">Order Type</div>
                            <div className="col-md-8">{quotation.quotation_item_list[0].service_type}</div>
                          </div>
                        )}
                        <div className="row mb-2">
                          <div className="col-md-4 text-secondary">Sales Name</div>
                          <div className="col-md-8">{iou.sales_name}</div>
                        </div>
                        <div className="row mb-2">
                          <div className="col-md-4 text-secondary">Admin Name</div>
                          <div className="col-md-8">{iou.atb_name}</div>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="card mb-2">
                          <div className="card-body">
                            <div className="d-flex align-items-center justify-content-between">
                              <p className="fs-14 mb-0"><strong>Quotation</strong></p>
                              <a href={"/quotation/detail/" + quotation.id_quotation}>{quotation.quotation_code} <i className="bi bi-link-45deg ms-1" /></a>
                            </div>
                          </div>
                        </div>
                        <div className="card mb-2">
                          <div className="card-body">
                            <div className="d-flex align-items-center justify-content-between">
                              <p className="fs-14 mb-0"><strong>Sales Order</strong></p>
                              <a href={"/salesorder/detail/" + salesOrder.id_so}>{salesOrder.so_code} <i className="bi bi-link-45deg ms-1" /></a>
                            </div>
                          </div>
                        </div>
                        <div className="card mb-2">
                          <div className="card-body">
                            <div className="d-flex align-items-center justify-content-between mb-2">
                              <p className="fs-14 mb-0"><strong>Cost &amp; Profit Summary</strong></p>
                            </div>
                            <div className="p-2">
                              <div className="row">
                                <div className="col-md-6 text-secondary">Total Cost</div>
                                <div className="col-md-6 text-end">{priceFormat(quotation.quotation_cost, quotation.quotation_currency)}</div>
                              </div>
                              <hr />
                              <div className="row mb-2">
                                <div className="col-md-6 text-secondary">Subtotal Sell (Pre-Discount, Pre-Tax)</div>
                                <div className="col-md-6 text-end">{priceFormat(quotation.quotation_subtotal, quotation.quotation_currency)}</div>
                              </div>
                              <div className="row mb-2">
                                <div className="col-md-6 text-secondary">Discount on QT {quotation.quotation_discount_price > 0 ? (" (" + priceFormat(quotation.quotation_discount_rate) + "%)") : ""}</div>
                                <div className="col-md-6 text-end"><span className="text-danger">{quotation.quotation_discount_price > 0 ? ("-" + priceFormat(quotation.quotation_discount_price, quotation.quotation_currency)) : "0"}</span></div>
                              </div>
                              <div className="row">
                                <div className="col-md-6 text-secondary">Net Amount Before Tax</div>
                                <div className="col-md-6 text-end">{priceFormat(quotation.quotation_total, quotation.quotation_currency)}</div>
                              </div>
                              <hr />
                              <div className="row">
                                <div className="col-md-6 text-secondary">Estimated  Gross Profit <strong>{" (" + quotation.quotation_margin_rate + "%)"}</strong></div>
                                <div className="col-md-6 text-end">{priceFormat(quotation.quotation_margin_price, quotation.quotation_currency)}</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </section>
                )}

                {iou && (
                  <section className="mb-4">
                    <div className="card mb-4">
                      <div className="card-body">
                        <div className="d-flex align-items-center justify-content-between mb-2">
                          <p className="fs-14 mb-0"><strong>{iou.subcompany_name}</strong></p>
                        </div>
                        <div className="p-2">
                          <div className="row mb-2">
                            <div className="col-md-2 text-secondary">Legal Name</div>
                            <div className="col-md-10">{iou.subcompany_billing_name}</div>
                          </div>
                          <div className="row mb-2">
                            <div className="col-md-2 text-secondary">Billing Address</div>
                            <div className="col-md-10">{iou.subcompany_billing_address}</div>
                          </div>
                          <div className="row mb-2">
                            <div className="col-md-2 text-secondary">Mailing Address</div>
                            <div className="col-md-10">{iou.subcompany_address}</div>
                          </div>
                          <div className="row mb-2">
                            <div className="col-md-2 text-secondary">Phone Number</div>
                            <div className="col-md-10">{iou.subcompany_phone}</div>
                          </div>
                          <div className="row mb-2">
                            <div className="col-md-2 text-secondary">Fax Number</div>
                            <div className="col-md-10">{iou.subcompany_fax}</div>
                          </div>
                          <div className="row">
                            <div className="col-md-2 text-secondary">NPWP Number</div>
                            <div className="col-md-10">{iou.subcompany_npwp_number}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </section>
                )}

                {iou && (
                  <section className="mb-4">
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <div className="fs-24">SKU List</div>
                    </div>
                    <div className="bg-white border-radius-4 p-4">
                      <div className="table-responsive">
                        <table className="table table-hover bordered mb-0">
                          <thead>
                            <tr>
                              <th className="bg-gold w-30">#</th>
                              <th className="bg-gold">SKUs</th>
                              <th className="bg-gold w-85">Period</th>
                              <th className="bg-gold w-85">Qty</th>
                              <th className="bg-gold w-105">Cost Price / Unit</th>
                              <th className="bg-gold w-105">Sell Price / Unit</th>
                              <th className="bg-gold w-105">Discount</th>
                              <th className="bg-gold w-125">Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            {iou.iou_items.map((iouItem, p) => (
                              <tr className={iouItem.action === 'Delete' ? 'd-none' : ''} key={"iou-item-" + p}>
                                <td valign="middle" align="center">{p + 1}</td>
                                <td valign="middle">
                                  <div className="d-flex align-items-center justify-content-between mb-2">
                                    <p className="mb-0"><strong>{iouItem.product_display_name}</strong></p>
                                    <button className="btn btn-sm btn-outline-secondary" onClick={() => copyItem(p)}><i className="bi bi-copy" /></button>
                                  </div>
                                  {iouItem.product_display_description && iouItem.product_display_description.split('\n').map((description, d) => (
                                    <div className="row mb-1" key={"description-" + d}>
                                      {description.split(':').length > 1 ? (
                                        <div className="col-4">{description.split(':')[0].trim()}</div>
                                      ) : (
                                        <div className="col-12">{description.trim()}</div>
                                      )}
                                      {description.split(':').length > 1 && (
                                        <div className="col-8">
                                          <div className="d-flex">
                                            <span className="me-2">:</span>
                                            <div>{description.split(':').slice(1).join(':').trim()}</div>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                  {iouItem.product_type === 'Subscription' ? (
                                    <div>
                                      <hr />
                                      <div className="row mb-1">
                                        <div className="col-4">This QT Order</div>
                                        <div className="col-8">
                                          <div className="d-flex">
                                            <span className="me-2">:</span>
                                            <div>{iou.quotation_code}</div>
                                          </div>
                                        </div>
                                      </div>
                                      {iouItem.service_type === 'Additional' && (
                                        <div className="row mb-1">
                                          <div className="col-4">Period - Current Active Subscription</div>
                                          <div className="col-8">
                                            <div className="d-flex">
                                              <span className="me-2">:</span>
                                              <div>{iouItem.licence_start_period ? (format(new Date(iouItem.licence_start_period), 'd MMMM yyyy') + " - " + format(new Date(iouItem.licence_end_period), 'd MMMM yyyy')) : ''}</div>
                                            </div>
                                          </div>
                                        </div>
                                      )}
                                      <div className="row mb-1">
                                        <div className="col-4">Period - for this QT Order</div>
                                        <div className="col-8">
                                          <div className="d-flex">
                                            <span className="me-2">:</span>
                                            <div>{format(new Date(iouItem.start_term_period), 'd MMMM yyyy') + " - " + format(new Date(iouItem.end_term_period), 'd MMMM yyyy')}</div>
                                          </div>
                                        </div>
                                      </div>
                                      <div className="row mb-1">
                                        <div className="col-4">Schedule</div>
                                        <div className="col-8">
                                          <div className="d-flex">
                                            <span className="me-2">:</span>
                                            <div>{iou.term_number.replace('Term ', '') + " of " + iou.term_count}</div>
                                          </div>
                                        </div>
                                      </div>
                                      <div className="row mb-1">
                                        <div className="col-4">Term Payment</div>
                                        <div className="col-8">
                                          <div className="d-flex">
                                            <span className="me-2">:</span>
                                            <div>{format(new Date(iou.start_term_period), 'd MMMM yyyy') + " - " + format(new Date(iou.end_term_period), 'd MMMM yyyy')}</div>
                                          </div>
                                        </div>
                                      </div>
                                      <div className="row mb-1">
                                        <div className="col-4">#User of this QT</div>
                                        <div className="col-8">
                                          <div className="d-flex">
                                            <span className="me-2">:</span>
                                            <div>{iouItem.product_qty + " Unit"}</div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  ) : (
                                    <div>
                                      <hr />
                                      <div className="row mb-1">
                                        <div className="col-4">This QT Order</div>
                                        <div className="col-8">
                                          <div className="d-flex">
                                            <span className="me-2">:</span>
                                            <div>{iou.quotation_code}</div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </td>
                                <td valign="middle" align="center">{iouItem.contract_long_period} Month(s)</td>
                                <td valign="middle" align="center">{iouItem.product_qty} Unit(s)</td>
                                <td valign="middle" align="center">
                                  <div className="d-flex flex-column justify-content-between py-3">
                                    <span>{priceFormat(iouItem.product_cost, iouItem.product_currency)}</span>
                                    {iouItem.product_currency !== iouItem.product_currency_original && (<hr className="my-3" />)}
                                    {iouItem.product_currency !== iouItem.product_currency_original && (<span className="text-secondary">{priceFormat(iouItem.product_cost_original, iouItem.product_currency_original)}</span>)}
                                  </div>
                                </td>
                                <td valign="middle" align="center">
                                  <div className="d-flex flex-column justify-content-between py-3">
                                    <span>{priceFormat(iouItem.product_price, iouItem.product_currency)}</span>
                                    {iouItem.product_currency !== iouItem.product_currency_original && (<hr className="my-3" />)}
                                    {iouItem.product_currency !== iouItem.product_currency_original && (<span className="text-secondary">{priceFormat(iouItem.product_price_original, iouItem.product_currency_original)}</span>)}
                                  </div>
                                </td>
                                <td valign="middle" align="center">
                                  {iouItem.product_discount_price > 0 ? (
                                    <div className="d-flex flex-column justify-content-between pt-3">
                                      <span className="text-danger">-{priceFormat(iouItem.product_discount_price, iouItem.product_currency)}</span>
                                      {iouItem.product_discount_price > 0 && (<span className="text-secondary">{priceFormat(iouItem.product_discount_rate, "%")}</span>)}
                                    </div>
                                  ) : 'N/A'}
                                  <br />
                                  <span className="text-secondary">on Item</span>
                                  <hr className="my-3" />
                                  {iouItem.product_discount_prorate > 0 ? (
                                    <div className="d-flex flex-column justify-content-between py-3">
                                      <span className="text-danger">-{priceFormat(iouItem.product_discount_prorate, iouItem.product_currency)}</span>
                                    </div>
                                  ) : 'N/A'}
                                  <br />
                                  <span className="text-secondary">on QT</span>
                                </td>
                                <td valign="middle" align="end">
                                  {priceFormat(iouItem.product_grand_total, iouItem.product_currency)}
                                  <div className="d-none">
                                    <div className="d-flex justify-content-between"><span>product_cost_original:</span><span>{priceFormat(iouItem.product_cost_original)}</span></div>
                                    <div className="d-flex justify-content-between"><span>product_cost:</span><span>{priceFormat(iouItem.product_cost)}</span></div>
                                    <div className="d-flex justify-content-between"><span>product_price_original:</span><span>{priceFormat(iouItem.product_price_original)}</span></div>
                                    <div className="d-flex justify-content-between"><span>product_price:</span><span>{priceFormat(iouItem.product_price)}</span></div>
                                    <div className="d-flex justify-content-between"><span>product_subtotal:</span><span>{priceFormat(iouItem.product_subtotal)}</span></div>
                                    <div className="d-flex justify-content-between"><span>product_discount_price:</span><span>{priceFormat(iouItem.product_discount_price)}</span></div>
                                    <div className="d-flex justify-content-between"><span>product_discount_rate:</span><span>{priceFormat(iouItem.product_discount_rate)}</span></div>
                                    <div className="d-flex justify-content-between"><span>product_total:</span><span>{priceFormat(iouItem.product_total)}</span></div>
                                    <div className="d-flex justify-content-between"><span>product_total_prorate:</span><span>{priceFormat(iouItem.product_total_prorate)}</span></div>
                                    <div className="d-flex justify-content-between"><span>product_discount_prorate:</span><span>{priceFormat(iouItem.product_discount_prorate)}</span></div>
                                    <div className="d-flex justify-content-between"><span>product_grand_total:</span><span>{priceFormat(iouItem.product_grand_total)}</span></div>
                                    <div className="d-flex justify-content-between"><span>product_margin_price:</span><span>{priceFormat(iouItem.product_margin_price)}</span></div>
                                    <div className="d-flex justify-content-between"><span>product_margin_rate:</span><span>{priceFormat(iouItem.product_margin_rate)}</span></div>
                                    <div className="d-flex justify-content-between"><span>contract_long_period:</span><span>{priceFormat(iouItem.contract_long_period)}</span></div>
                                    <div className="d-flex justify-content-between"><span>action:</span><span>{iouItem.action}</span></div>
                                  </div>
                                </td>
                              </tr>
                            )
                            )}
                          </tbody>
                          <tfoot>
                            <tr>
                              <td className="text-end" colSpan={7}>
                                Total Before Tax
                              </td>
                              <td className="text-end">
                                <strong>{priceFormat(iou.iou_items.reduce((sum, iouItem) => sum + parseFloat((iouItem.product_grand_total)), 0), iou.iou_currency)}</strong>
                              </td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    </div>
                  </section>
                )}

                {iou && (
                  <section className="mb-4">
                    <div className="row d-flex align-items-baseline mb-2">
                      <div className="col-md-2 text-secondary">Billing Documents</div>
                      <div className="col-md-10">
                        <div className="table-responsive">
                          <table className="table table-hover bordered mb-2">
                            <thead>
                              <tr>
                                <th className="bg-gold w-30">#</th>
                                <th className="bg-gold text-start">Title</th>
                              </tr>
                            </thead>
                            <tbody>
                              {iou.request_invoice_documents && iou.request_invoice_documents.map((document, d) =>
                                <tr key={"iou-document-" + d}>
                                  <td valign="middle" align="center">{d + 1}</td>
                                  <td valign="middle">{document}</td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                    <div className="row mb-2">
                      <div className="col-md-2 text-secondary">Attached Transaction Reference</div>
                      <div className="col-md-10">
                        <div className="table-responsive">
                          <table className="table table-hover bordered mb-2">
                            <thead>
                              <tr>
                                <th className="bg-gold w-30">#</th>
                                <th className="bg-gold text-start">Title</th>
                                <th className="bg-gold w-120">Status</th>
                                <th className="bg-gold text-start">File</th>
                              </tr>
                            </thead>
                            <tbody>
                              {iou.request_invoice_attachments && iou.request_invoice_attachments.map((attachment, a) =>
                                <tr key={"iou-attachment-" + a}>
                                  <td valign="middle" align="center">{a + 1}</td>
                                  <td valign="middle">{attachment.name}</td>
                                  <td valign="middle" align="center">{attachment.status}</td>
                                  <td valign="middle">
                                    {attachment.files && (
                                      <a className="ellipsis" href={attachment.files[0].fileUrl} target="_blank" rel="noopener noreferrer">{shortenUrl(attachment.files[0].fileUrl, 50)} <i className="bi bi-link-45deg ms-2" /></a>
                                    )}
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                    <div className="row mb-2">
                      <div className="col-md-2 text-secondary">Quotation Remarks</div>
                      <div className="col-md-10">{iou.quotation_remarks}</div>
                    </div>
                    <div className="row mb-2">
                      <div className="col-md-2 text-secondary">Sales Order Remarks</div>
                      <div className="col-md-10">{iou.so_remarks}</div>
                    </div>
                    <div className="row mb-2">
                      <div className="col-md-2 text-secondary">Additional Notes</div>
                      <div className="col-md-10">{iou.request_invoice_remarks}</div>
                    </div>
                  </section>
                )}

                {iou && (
                  <section className="mb-4">
                    {iou.iou_status === 'Notification Sent' ? (
                      <div className="mt-4 mb-2">
                        <div className="d-flex align-items-center justify-content-between mb-3">
                          <div className="fs-24">Invoice Detail</div>
                        </div>
                        <div className="row">
                          <div className="col-md-6">
                            <div className="row align-items-baseline form-group mb-2">
                              <div className="col-md-4">
                                <label>Number <span className="text-danger">*</span></label>
                              </div>
                              <div className="col-md-8">
                                <input className="form-control" type="text" value={iou.invoice_number} onChange={(e) => changeIouData('invoice_number', e.target.value)} required />
                              </div>
                            </div>
                            <div className="row align-items-baseline form-group mb-2">
                              <div className="col-md-4">
                                <label>Amount <span className="text-danger">*</span></label>
                              </div>
                              <div className="col-md-8">
                                <div className="input-group">
                                  <input className="form-control text-end" type="text" value={priceFormat(iou.invoice_amount)} onChange={(e) => changeIouData('invoice_amount', numberFormat(e.target.value))} required />
                                  <span className="input-group-text">{iou.iou_currency}</span>
                                </div>
                              </div>
                            </div>
                            <div className="row align-items-baseline form-group mb-2">
                              <div className="col-md-4">
                                <label>Due Date <span className="text-danger">*</span></label>
                              </div>
                              <div className="col-md-8">
                                <input className="form-control" type="date" value={iou.invoice_due_date} onChange={(e) => changeIouData('invoice_due_date', e.target.value)} required />
                              </div>
                            </div>
                            <div className="row align-items-baseline form-group mb-2">
                              <div className="col-md-4">
                                <label>File</label>
                              </div>
                              <div className="col-md-8">
                                <input className="form-control" type="file" multiple={true} accept=".pdf, .png, .jpg, .jpeg, .gif, .webp, .svg, .bmp, .avif, .csv, .xls, .xlsx" onChange={(e) => setInvoiceAttachments(e.target.files.length > 0 ? Array.from(e.target.files) : null)} />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      (iou.iou_status === 'Invoice Issued' || iou.iou_status === 'Paid in Full') && (
                        <div className="mt-4 mb-2">
                          <div className="d-flex align-items-center justify-content-between mb-3">
                            <div className="fs-24">Invoice Detail</div>
                          </div>
                          <div className="row">
                            <div className="col-md-6">
                              <div className="row align-items-baseline form-group mb-2">
                                <div className="col-md-4">
                                  <label>Number</label>
                                </div>
                                <div className="col-md-8">{iou.invoice_number}</div>
                              </div>
                              <div className="row align-items-baseline form-group mb-2">
                                <div className="col-md-4">
                                  <label>Amount</label>
                                </div>
                                <div className="col-md-8">{priceFormat(iou.invoice_amount, iou.iou_currency)}</div>
                              </div>
                              <div className="row align-items-baseline form-group mb-2">
                                <div className="col-md-4">
                                  <label>Due Date</label>
                                </div>
                                <div className="col-md-8">{iou.invoice_due_date ? format(iou.invoice_due_date, 'd MMM yyyy') : ''}</div>
                              </div>
                              <div className="row align-items-baseline form-group mb-2">
                                <div className="col-md-4">
                                  <label>File</label>
                                </div>
                                <div className="col-md-8">
                                  {iou.invoice_attachments ? (
                                    JSON.parse(iou.invoice_attachments).map((attachment, a) =>
                                      <div key={"invoice-attachment-" + a}>
                                        <a className="ellipsis" href={attachment.fileUrl} target="_blank" rel="noopener noreferrer">{shortenUrl(attachment.fileUrl, 50)} <i className="bi bi-link-45deg ms-2" /></a>
                                      </div>
                                    )
                                  ) : (
                                    crmUser.user_level !== 'View' && (
                                      <div className="input-group">
                                        <input className="form-control" type="file" multiple={true} accept=".pdf, .png, .jpg, .jpeg, .gif, .webp, .svg, .bmp, .avif, .csv, .xls, .xlsx" onChange={(e) => setInvoiceAttachments(e.target.files.length > 0 ? Array.from(e.target.files) : null)} />
                                        <button className="btn btn-outline-primary" type="button" onClick={() => updateInvoiceAttachments()} disabled={isLoadingSubmit || !invoiceAttachments}>{isLoadingSubmit ? 'Loading...' : 'Upload'}</button>
                                      </div>
                                    )
                                  )}
                                </div>
                              </div>
                              <div className="row align-items-baseline form-group mb-2">
                                <div className="col-md-4">
                                  <label>Input Date</label>
                                </div>
                                <div className="col-md-8">{iou.invoice_input_date ? format(iou.invoice_input_date, 'd MMM yyyy HH:mm:ss') : ''}</div>
                              </div>
                              <div className="row align-items-baseline form-group mb-2">
                                <div className="col-md-4">
                                  <label>Input By</label>
                                </div>
                                <div className="col-md-8">{iou.finance_name}</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )
                    )}

                    {iou.iou_status === 'Invoice Issued' ? (
                      <div className="mt-4 mb-2">
                        <div className="d-flex align-items-center justify-content-between mb-3">
                          <div className="fs-24">Payment Detail</div>
                        </div>
                        <div className="row">
                          <div className="col-md-6">
                            <div className="row align-items-baseline form-group mb-2">
                              <div className="col-md-4">
                                <label>Paid Date <span className="text-danger">*</span></label>
                              </div>
                              <div className="col-md-8">
                                <input className="form-control" type="date" value={iou.payment_paid_date} onChange={(e) => changeIouData('payment_paid_date', e.target.value)} required />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      (iou.iou_status === 'Paid in Full') && (
                        <div className="mt-4 mb-2">
                          <div className="d-flex align-items-center justify-content-between mb-3">
                            <div className="fs-24">Payment Detail</div>
                          </div>
                          <div className="row">
                            <div className="col-md-6">
                              <div className="row align-items-baseline form-group mb-2">
                                <div className="col-md-4">
                                  <label>Paid Date</label>
                                </div>
                                <div className="col-md-8">{iou.payment_paid_date ? format(iou.payment_paid_date, 'd MMM yyyy') : ''}</div>
                              </div>
                              <div className="row align-items-baseline form-group mb-2">
                                <div className="col-md-4">
                                  <label>Input Date</label>
                                </div>
                                <div className="col-md-8">{iou.payment_input_date ? format(iou.payment_input_date, 'd MMM yyyy HH:mm:ss') : ''}</div>
                              </div>
                              <div className="row align-items-baseline form-group mb-2">
                                <div className="col-md-4">
                                  <label>Input By</label>
                                </div>
                                <div className="col-md-8">{iou.payment_name}</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )
                    )}
                  </section>
                )}

                {crmUser.user_level !== 'View' && (
                  <>
                    {iou && (iou.iou_status === 'Notification Sent') && (
                      <section className="mb-4">
                        <div className="d-flex align-items-center justify-content-end">
                          <button className="btn btn-link" type="button" onClick={() => window.location.href = '/invoicerequest/'} disabled={isLoadingSubmit}>Cancel</button>
                          <button className="btn btn-outline-primary" type="button" onClick={() => updateInvoice()} disabled={isLoadingSubmit}>{isLoadingSubmit ? 'Loading...' : 'Update'}</button>
                        </div>
                      </section>
                    )}

                    {iou && (iou.iou_status === 'Invoice Issued') && (
                      <section className="mb-4">
                        <div className="d-flex align-items-center justify-content-end">
                          <button className="btn btn-link" type="button" onClick={() => window.location.href = '/invoicerequest/'} disabled={isLoadingSubmit}>Cancel</button>
                          <button className="btn btn-outline-primary" type="button" onClick={() => updatePayment()} disabled={isLoadingSubmit}>{isLoadingSubmit ? 'Loading...' : 'Update'}</button>
                        </div>
                      </section>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceRequestDetail;


